import type * as React from "react";
import {
  Calendar,
  Eye,
  MoreVertical,
  Pencil,
  Power,
  RotateCw,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { TrashBinMinimalisticLinearIcon } from "../../icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../DropdownMenu";
import type { DataTableActionMenuLabels } from "./types";

export interface DataTableActionMenuProps {
  onView?: (() => void) | undefined;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  onDeActivate?: (() => void) | undefined;
  onResendActivation?: (() => void) | undefined;
  onExtend?: (() => void) | undefined;
  labels: DataTableActionMenuLabels;
  className?: string | undefined;
  children?: React.ReactNode;
}

export function DataTableActionMenu({
  onView,
  onEdit,
  onDelete,
  onDeActivate,
  onResendActivation,
  onExtend,
  labels,
  className,
  children,
}: DataTableActionMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={labels.trigger}
          className={cn(
            "inline-flex size-8 items-center justify-center rounded-md text-secondary-icon transition-[background-color,color,scale] duration-140 ease-emphasized hover:bg-container-hover hover:text-text active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
            className,
          )}
        >
          <MoreVertical className="size-4" />
          <span className="sr-only">{labels.trigger}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        {onView && labels.view ? (
          <DropdownMenuItem onSelect={onView}>
            <Eye className="size-4 me-2" />
            {labels.view}
          </DropdownMenuItem>
        ) : null}

        {onEdit && labels.edit ? (
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil className="size-4 me-2" />
            {labels.edit}
          </DropdownMenuItem>
        ) : null}

        {onResendActivation && labels.resendActivation ? (
          <DropdownMenuItem onSelect={onResendActivation}>
            <RotateCw className="size-4 me-2" />
            {labels.resendActivation}
          </DropdownMenuItem>
        ) : null}

        {onExtend && labels.extend ? (
          <DropdownMenuItem onSelect={onExtend}>
            <Calendar className="size-4 me-2" />
            {labels.extend}
          </DropdownMenuItem>
        ) : null}

        {onDeActivate && labels.deactivate ? (
          <DropdownMenuItem destructive onSelect={onDeActivate}>
            <Power className="size-4 me-2" />
            {labels.deactivate}
          </DropdownMenuItem>
        ) : null}

        {onDelete && labels.delete ? (
          <DropdownMenuItem destructive onSelect={onDelete}>
            <TrashBinMinimalisticLinearIcon className="size-4 me-2" />
            {labels.delete}
          </DropdownMenuItem>
        ) : null}

        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
